import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';

/**
 * Parse site geolocation string (format: "lat,lng")
 * @param {string} geolocation - Geolocation string
 * @returns {Object} Object with location_lat and location_lng, or null
 */
function parseSiteGeolocation(geolocation) {
    if (!geolocation || typeof geolocation !== 'string') {
        return null;
    }

    const trimmed = geolocation.trim();
    if (trimmed === '' || trimmed === '0,0') {
        return null;
    }

    const parts = trimmed.split(',');
    if (parts.length === 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
            return { location_lat: lat, location_lng: lng };
        }
    }
    return null;
}

/**
 * Convert a site object to location format
 * @param {Object} site - Site object with site_geolocation
 * @returns {Object} Normalized location object
 */
function convertSiteToLocation(site) {
    const coords = parseSiteGeolocation(site.site_geolocation);
    if (!coords) {
        return null;
    }

    return {
        location_id: site.site_id,
        location_name: site.site_name,
        location_lat: coords.location_lat,
        location_lng: coords.location_lng,
        markerType: 'site',
        sourceObject: site
    };
}

/**
 * Normalize zone location to standard format
 * @param {Object} zone - Zone object
 * @returns {Object} Normalized location object
 */
function normalizeZoneLocation(zone) {
    return {
        ...zone,
        markerType: 'zone',
        sourceObject: zone
    };
}

class LocationMapManager {
    constructor(locations = []) {
        this.locations = locations;
    }

    /**
     * Add a location to the map
     * @param {Object} location - Location object with lat, lng, location_id, location_name
     */
    addLocation(location) {
        if (location && location.location_lat !== undefined && location.location_lng !== undefined) {
            this.locations.push(location);
        }
    }

    /**
     * Remove a location from the map
     * @param {string} locationId - Location ID to remove
     */
    removeLocation(locationId) {
        this.locations = this.locations.filter(loc => loc.location_id !== locationId);
    }

    /**
     * Get all valid locations with coordinates
     */
    getValidLocations() {
        console.log('Filtering valid locations from:', this.locations);
        return this.locations.filter(
            loc => loc.location_lat !== undefined &&
                loc.location_lng !== undefined &&
                loc.location_lat !== null &&
                loc.location_lng !== null &&
                !isNaN(parseFloat(loc.location_lat)) &&
                !isNaN(parseFloat(loc.location_lng))
        );
    }

    /**
     * Calculate map bounds from all locations
     */
    calculateBounds(locations = this.locations) {
        const validLocs = locations.length > 0 ? locations : this.getValidLocations();

        if (validLocs.length === 0) {
            return null;
        }

        let minLat = parseFloat(validLocs[0].location_lat);
        let maxLat = parseFloat(validLocs[0].location_lat);
        let minLng = parseFloat(validLocs[0].location_lng);
        let maxLng = parseFloat(validLocs[0].location_lng);

        validLocs.forEach(loc => {
            const lat = parseFloat(loc.location_lat);
            const lng = parseFloat(loc.location_lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
        });

        return {
            ne: { lat: maxLat, lng: maxLng },
            sw: { lat: minLat, lng: minLng }
        };
    }

    /**
     * Get center point from all locations
     */
    getCenter(locations = this.getValidLocations()) {
        if (locations.length === 0) {
            return { lat: 37.0902, lng: -95.7129 }; // Default to USA center
        }

        const avgLat = locations.reduce((sum, loc) => sum + parseFloat(loc.location_lat), 0) / locations.length;
        const avgLng = locations.reduce((sum, loc) => sum + parseFloat(loc.location_lng), 0) / locations.length;

        return { lat: avgLat, lng: avgLng };
    }
}

const LocationMap = ({
    locations = [],
    selectedLocationId = null,
    onLocationSelect = () => { },
    mapHeight = '400px',
    readOnly = false
}) => {
    const mapRef = useRef(null);

    // Initialize map manager
    const mapManager = useMemo(() => {
        return new LocationMapManager(locations);
    }, [locations]);

    const validLocations = useMemo(() => {
        return mapManager.getValidLocations();
    }, [mapManager]);

    const mapCenter = useMemo(() => {
        return mapManager.getCenter(validLocations);
    }, [mapManager, validLocations]);

    // Fit bounds when map loads or locations change
    useEffect(() => {
        if (mapRef.current && validLocations.length > 0) {
            const bounds = mapManager.calculateBounds(validLocations);
            if (bounds && mapRef.current.fitBounds) {
                const googleBounds = new window.google.maps.LatLngBounds(
                    { lat: bounds.sw.lat, lng: bounds.sw.lng },
                    { lat: bounds.ne.lat, lng: bounds.ne.lng }
                );
                // Add padding to bounds
                mapRef.current.fitBounds(googleBounds, 50);
            }
        }
    }, [validLocations, mapManager]);

    const handleMapLoad = useCallback((map) => {
        mapRef.current = map;
        // Fit bounds when map initially loads
        if (validLocations.length > 0) {
            const bounds = mapManager.calculateBounds(validLocations);
            if (bounds && map.fitBounds) {
                const googleBounds = new window.google.maps.LatLngBounds(
                    { lat: bounds.sw.lat, lng: bounds.sw.lng },
                    { lat: bounds.ne.lat, lng: bounds.ne.lng }
                );
                // Add padding to bounds
                map.fitBounds(googleBounds, 50);
                window.google.maps.event.addListenerOnce(map, 'idle', () => {
                    const currentZoom = map.getZoom();
                    map.setZoom(currentZoom - 1); // zoom out one level
                });
            }
        }
    }, [validLocations, mapManager]);

    const mapOptions = useMemo(() => ({
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        styles: [
            {
                elementType: 'labels',
                stylers: [{ visibility: 'on' }],
            },
        ],
    }), []);

    const handleMarkerClick = useCallback((locationId) => {
        if (!readOnly) {
            onLocationSelect(locationId);
        }
    }, [readOnly, onLocationSelect]);

    const getMarkerIcon = (location, isSelected) => {
        if (isSelected) {
            return 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'; // Selected - Blue
        }

        // Color based on working status
        if (location.markerType === 'zone') {
            if (location.is_working) {
                return 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'; // Working camera - Green
            } else {
                return 'http://maps.google.com/mapfiles/ms/icons/grey-dot.png'; // Not working - Gray
            }
        }

        // Sites use default red
        return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
    };

    if (validLocations.length === 0) {
        return (
            <div style={{
                height: mapHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                color: '#999'
            }}>
                <span>No locations with coordinates available</span>
            </div>
        );
    }

    return (
        <div style={{
            height: mapHeight,
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
            <GoogleMap
                zoom={2}
                center={mapCenter}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                options={mapOptions}
                onLoad={handleMapLoad}
            >
                {validLocations.map(location => (
                    <MarkerF
                        key={location.location_id}
                        position={{
                            lat: parseFloat(location.location_lat),
                            lng: parseFloat(location.location_lng)
                        }}
                        onClick={() => handleMarkerClick(location.location_id)}
                        label={{
                            text: location.location_name,
                            color: '#6b32b4',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            className: 'map-marker-label'
                        }}
                        options={{
                            title: location.location_name,
                            cursor: readOnly ? 'default' : 'pointer',
                            icon: getMarkerIcon(location, selectedLocationId === location.location_id)
                        }}
                    >
                    </MarkerF>
                ))}
            </GoogleMap>
        </div>
    );
};

export { LocationMap, LocationMapManager, convertSiteToLocation, normalizeZoneLocation, parseSiteGeolocation };
export default LocationMap;
